import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const milestones = [
  {
    year: '2013',
    title: 'Founded Custom Group',
    description:
      'Launched in Ballito, KZN. First mandates focused on operational systems for property and logistics businesses — built to measurable outcomes, not specifications.',
  },
  {
    year: '2016',
    title: 'Enterprise Engagements',
    description:
      'Expanded to serve larger organisations including logistics companies and retail chains. Introduced multi-stakeholder systems and payment infrastructure at scale.',
  },
  {
    year: '2019',
    title: 'National Practice',
    description:
      'Broadened scope to serve clients nationally. Delivered production systems across property, education, and manufacturing sectors.',
  },
  {
    year: '2022',
    title: 'AI Integration Practice',
    description:
      'Built the first AI-assisted delivery capability. Began advising clients on where AI generates real operational return — as distinct from where it creates noise.',
  },
  {
    year: '2024',
    title: 'Enterprise AI Architecture',
    description:
      'First biometric authentication integration and first enterprise AI platform delivering on-premise inference at scale for a client with 500+ concurrent users.',
  },
  {
    year: '2025',
    title: 'AI Advisory Practice',
    description:
      'Repositioned as an AI advisory firm. Advising leadership teams on AI capital allocation, local deployment strategy, and ROI validation across sectors.',
  },
];

const expertiseBadges = [
  'AI Strategy',
  'Economic Modelling',
  'Local AI Deployment',
  'Data Sovereignty',
  'Board Advisory',
  'Enterprise Architecture',
  'ROI Frameworks',
  'Risk Assessment',
];

export const FounderPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    document.title = 'Douglas Bothma — AI Advisory | Custom Group';
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#f5f5f5]">
      {/* Sticky Nav */}
      <nav className="bg-[#0a0a0a] border-b border-[#222222] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="text-lg font-semibold tracking-tight text-[#f5f5f5]">
              Custom Group
            </Link>

            {/* Desktop links */}
            <div className="hidden md:flex items-center gap-10 text-sm font-medium text-[#888888]">
              <Link to="/blog" className="hover:text-[#f5f5f5] transition-colors">Insights</Link>
              <Link to="/#approach" className="hover:text-[#f5f5f5] transition-colors">Our Approach</Link>
              <Link to="/#access" className="hover:text-[#f5f5f5] transition-colors">Advisory Access</Link>
              <Link
                to="/apply"
                className="bg-[#E8651A] text-[#0a0a0a] px-5 py-2 text-sm font-semibold hover:bg-[#C9520E] transition-colors"
              >
                Apply for Access
              </Link>
            </div>

            {/* Mobile hamburger */}
            <button
              className="md:hidden p-2 text-[#888888] hover:text-[#f5f5f5]"
              onClick={() => setMobileMenuOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>

          {/* Mobile menu */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-[#222222] py-6 flex flex-col gap-4 text-sm font-medium">
              <Link to="/blog" className="text-[#888888] hover:text-[#f5f5f5]" onClick={() => setMobileMenuOpen(false)}>Insights</Link>
              <Link to="/#approach" className="text-[#888888] hover:text-[#f5f5f5]" onClick={() => setMobileMenuOpen(false)}>Our Approach</Link>
              <Link to="/#access" className="text-[#888888] hover:text-[#f5f5f5]" onClick={() => setMobileMenuOpen(false)}>Advisory Access</Link>
              <Link
                to="/apply"
                className="bg-[#E8651A] text-[#0a0a0a] px-5 py-3 text-sm font-semibold text-center"
                onClick={() => setMobileMenuOpen(false)}
              >
                Apply for Access
              </Link>
            </div>
          )}
        </div>
      </nav>

      {/* Hero */}
      <section className="border-b border-[#222222] py-24 px-6 lg:px-8">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-start gap-16">
          {/* Avatar */}
          <div className="shrink-0">
            <img 
              src="/douglas-bothma.jpg" 
              alt="Douglas Bothma" 
              className="w-24 h-24 rounded-full object-cover border-2 border-[#E8651A]"
            />
          </div>

          {/* Intro */}
          <div>
            <p className="text-xs text-[#E8651A] tracking-widest uppercase mb-4">
              Founder &amp; Senior Advisor
            </p>
            <h1 className="text-4xl sm:text-5xl font-bold mb-4 tracking-tight">Douglas Bothma</h1>
            <p className="text-[#888888] text-base mb-6">
              Custom Group · Ballito, South Africa
            </p>
            <p className="text-[#888888] text-base leading-relaxed max-w-xl mb-8">
              Douglas advises leadership teams on AI capital allocation and implementation strategy.
              13+ years of enterprise software delivery — from single-location businesses to multi-site
              organisations with complex operational requirements. Every engagement is grounded in
              measurable economic outcome, not technology novelty.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="https://www.linkedin.com/in/douglasbothma/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border border-[#222222] text-[#f5f5f5] px-5 py-2.5 text-sm font-semibold hover:border-[#E8651A] transition-colors"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
                LinkedIn
              </a>
              <Link
                to="/apply"
                className="inline-flex items-center gap-2 bg-[#E8651A] text-[#0a0a0a] px-5 py-2.5 text-sm font-semibold hover:bg-[#C9520E] transition-colors"
              >
                Apply for Advisory Access →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Company History */}
      <section className="border-b border-[#222222]">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-20">
          <h2 className="text-2xl font-bold mb-3 tracking-tight">Company History</h2>
          <p className="text-[#888888] text-base mb-12">
            Consistent mission across twelve years: deliver measurable outcomes, not deliverables.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#222222]">
            {[
              {
                era: '2013 – 2019',
                name: 'Custom Group',
                desc: 'Founded in Ballito. Built operational systems for property, logistics, and professional services businesses. Fixed-price, outcome-led engagements from the outset.',
                accent: 'border-[#222222]',
              },
              {
                era: '2019 – 2024',
                name: 'Custom Group',
                desc: 'Expanded nationally. Complex multi-user systems, enterprise API integrations, and payment infrastructure. Delivered 20+ production systems across five sectors.',
                accent: 'border-[#222222]',
              },
              {
                era: '2024 – Present',
                name: 'Custom Group — AI Advisory',
                desc: 'Repositioned as an AI advisory practice. Advising leadership teams on AI strategy, ROI validation, local deployment, and data sovereignty.',
                accent: 'border-[#E8651A]',
              },
            ].map((phase) => (
              <div
                key={phase.name + phase.era}
                className={`bg-[#111111] border-t-2 ${phase.accent} p-8`}
              >
                <p className="text-xs text-[#888888] uppercase tracking-widest mb-3">{phase.era}</p>
                <h3 className="text-base font-bold text-[#f5f5f5] mb-3">{phase.name}</h3>
                <p className="text-[#888888] text-sm leading-relaxed">{phase.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote / Philosophy */}
      <section className="border-b border-[#222222]">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 py-20">
          <div className="w-8 h-px bg-[#E8651A] mb-10" />
          <blockquote className="text-2xl sm:text-3xl font-bold text-[#f5f5f5] leading-tight mb-6">
            "The question is never which AI tool to use. It's where in your organisation AI will generate real economic return — and how to implement it without creating new risk."
          </blockquote>
          <cite className="text-sm text-[#888888] not-italic tracking-wide uppercase">
            — Douglas Bothma, Founder
          </cite>
        </div>
      </section>

      {/* Timeline */}
      <section className="border-b border-[#222222]">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 py-20">
          <h2 className="text-2xl font-bold mb-12 tracking-tight">Key Milestones</h2>
          <div className="space-y-10">
            {milestones.map((m) => (
              <div key={m.year} className="flex gap-8 border-l border-[#222222] pl-8 relative">
                <div className="absolute left-0 top-0 -translate-x-1/2 w-px">
                  <div className="w-2 h-2 bg-[#E8651A] rounded-full -translate-x-0.5" />
                </div>
                <div>
                  <p className="text-xs text-[#E8651A] uppercase tracking-widest mb-1">{m.year}</p>
                  <h3 className="text-base font-bold text-[#f5f5f5] mb-2">{m.title}</h3>
                  <p className="text-[#888888] text-sm leading-relaxed">{m.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Expertise */}
      <section className="border-b border-[#222222]">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-20">
          <h2 className="text-2xl font-bold mb-4 tracking-tight">Expertise</h2>
          <p className="text-[#888888] text-base mb-10">
            Advisory disciplines built across 13 years of enterprise delivery.
          </p>
          <div className="flex flex-wrap gap-3">
            {expertiseBadges.map((badge) => (
              <span
                key={badge}
                className="border border-[#222222] text-[#888888] px-4 py-2 text-sm font-medium hover:border-[#E8651A] hover:text-[#f5f5f5] transition-colors"
              >
                {badge}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-b border-[#222222]">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-20">
          <div className="bg-[#111111] border border-[#222222] p-16">
            <h2 className="text-2xl font-bold mb-4 tracking-tight">Engage Douglas</h2>
            <p className="text-[#888888] text-base mb-8 max-w-xl">
              Advisory begins with an application. We review all applications within 2 business days.
              Engagements start from $7,500.
            </p>
            <Link
              to="/apply"
              className="inline-block bg-[#E8651A] text-[#0a0a0a] font-semibold px-8 py-4 hover:bg-[#C9520E] transition-colors text-sm"
            >
              Apply for Advisory Access →
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#222222] py-16 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-start gap-8">
          <div>
            <p className="text-[#f5f5f5] font-semibold mb-1">Custom Group · Ballito, South Africa</p>
            <p className="text-xs text-[#888888]">© 2026 Custom Group. All rights reserved.</p>
          </div>
          <div className="flex flex-wrap gap-8 text-sm text-[#888888]">
            <Link to="/blog" className="hover:text-[#f5f5f5] transition-colors">Insights</Link>
            <Link to="/founder" className="hover:text-[#f5f5f5] transition-colors">About</Link>
            <Link to="/apply" className="hover:text-[#f5f5f5] transition-colors">Apply</Link>
            <Link to="/terms" className="hover:text-[#f5f5f5] transition-colors">Terms</Link>
            <a
              href="https://www.linkedin.com/in/douglasbothma/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#f5f5f5] transition-colors"
            >
              LinkedIn
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};
